import IBasicInfoContract from "../models/contracts/BasicInfoContract";
import {
  FacetsViewModel,
  BasicInfoViewModel,
  FacetCategoryViewModel,
} from "../models/views";
import { MeasurementTypes } from "../models/MeasurementTypes";
import { FacetCategories } from "../models/FacetCategories";
import { sortArray } from "../../../utils/array";

const BASIC_INFO_FORM_BASE_MAPPING = {
  playingAgeFrom: "fromAge",
  playingAgeTo: "toAge",
  isVisibleActualAge: "isActualAgeVisible",
  isPregnant: "isPregnant",
  pregnantUntil: "untilPregnantDate",
  vocalAgeFrom: "vocalAgeFrom",
  vocalAgeTo: "vocalAgeTo",
  hasTwin: "haveTwin",
  hasDisability: "hasDisability",
  isVisibleDisability: "isVisibleDisability",
  disabilityInfo: "disabilityInfo",
  isVisibleGender: "isVisibleGender",
  genderInfo: "genderInfo",
  isVisibleHeritageAndEthnicity: "isVisibleHeritageAndEthnicity",
  isVisibleVisasAndPermits: "isVisibleVisasAndPermits",
  heritageAndEthnicityInfo: "heritageAndEthnicityInfo",
  makeUp: "makeUp",
};

const BASIC_INFO_FORM_DANCING_MEASUREMENTS_MAPPING = {
  [MeasurementTypes.Weight]: "weight",
  [MeasurementTypes.Chest]: "chestSize",
  [MeasurementTypes.Waist]: "waistSize",
  [MeasurementTypes.Hips]: "hipsSize",
  [MeasurementTypes.InsideLeg]: "insideLegSize",
  [MeasurementTypes.InsideArm]: "insideArmSize",
  [MeasurementTypes.Collar]: "collarSize",
  [MeasurementTypes.Hat]: "hatSize",
  [MeasurementTypes.Shoes]: "shoeSize",
  [MeasurementTypes.Dress]: "dressSize",
};

const BASIC_INFO_FORM_MEASUREMENTS_MAPPING = {
  [MeasurementTypes.Height]: "height",
  [MeasurementTypes.UpperVocalNote]: "upperNote",
  [MeasurementTypes.LowerVocalNote]: "lowerNote",
  ...BASIC_INFO_FORM_DANCING_MEASUREMENTS_MAPPING,
};

const BASIC_INFO_FORM_MULTIPLE_FACETS_MAPPING = {
  [FacetCategories.MembershipBodies]: "memberships",
  [FacetCategories.Nationality]: "nationalities",
  [FacetCategories.Appearance]: "appearances",
  [FacetCategories.Disability]: "disabilities",
  [FacetCategories.Gender]: "genders",
  [FacetCategories.HeritageAndEthnicity]: "heritageAndEthnicity",
  [FacetCategories.EthnicBackground]: "ethnicBackground",
  [FacetCategories.VoiceStyle]: "voiceStyle",
  [FacetCategories.VisasAndWorkingPermits]: "visasAndPermits",
};

const BASIC_INFO_FORM_SINGLE_FACETS_MAPPING = {
  [FacetCategories.EyeColour]: "eyeColor",
  [FacetCategories.HairColour]: "hairColor",
  [FacetCategories.HairLength]: "hairLength",
  [FacetCategories.FacialHair]: "facialHair",
  [FacetCategories.BeltNotes]: "beltNote",
  [FacetCategories.Gender]: "gender",
};

export class BasicInfoConverter {
  public static convertToView(
    basicInfo: IBasicInfoContract,
    facets: FacetsViewModel
  ): BasicInfoViewModel {
    let model = new BasicInfoViewModel();
    model = this.mapViewBasePart(model, basicInfo);
    model = this.mapViewMeasurements(model, basicInfo);
    model = this.mapViewFacets(model, basicInfo, facets);

    model.locations = sortArray(basicInfo.locations, [
      { expression: (item) => item.position, order: "asc" },
    ]).map((location) => ({
      id: location.googlePlaceId,
      name: location.value,
    }));

    return model;
  }

  public static convertToBusiness(
    model: BasicInfoViewModel
  ): IBasicInfoContract {
    let contract: IBasicInfoContract = {} as IBasicInfoContract;

    contract = this.mapBasePart(model, contract);
    contract = this.mapMeasurements(model, contract);
    contract = this.mapFacets(model, contract);

    contract.locations = model.locations.map((location, index) => ({
      googlePlaceId: location.id,
      value: location.name,
      position: index,
    }));

    return contract;
  }

  private static mapViewBasePart(
    model: BasicInfoViewModel,
    basicInfo: IBasicInfoContract
  ): BasicInfoViewModel {
    Object.keys(BASIC_INFO_FORM_BASE_MAPPING).forEach((key) => {
      const modelKey = BASIC_INFO_FORM_BASE_MAPPING[key];

      model[modelKey] = basicInfo[key];
    });

    return model;
  }

  private static mapViewMeasurements(
    model: BasicInfoViewModel,
    basicInfo: IBasicInfoContract
  ): BasicInfoViewModel {
    basicInfo.measurements.forEach((measurement) => {
      const modelKey =
        BASIC_INFO_FORM_MEASUREMENTS_MAPPING[measurement.measurementType];

      if (modelKey) {
        model[modelKey] = measurement.value;
      }
    });

    return model;
  }

  private static mapViewFacets(
    model: BasicInfoViewModel,
    basicInfo: IBasicInfoContract,
    facets: FacetsViewModel
  ): BasicInfoViewModel {
    basicInfo.facets.forEach((selectedFacet) => {
      Object.keys(facets).forEach((categoryKey) => {
        const category: FacetCategoryViewModel = facets[categoryKey];
        const facet = category.facets.find(
          (facet) => facet.id === selectedFacet.facetId
        );

        if (!facet) {
          return;
        }

        const keyInMultiple = Object.keys(
          BASIC_INFO_FORM_MULTIPLE_FACETS_MAPPING
        ).find((key) => key === category.systemName);
        const keyInSingle =
          !keyInMultiple &&
          Object.keys(BASIC_INFO_FORM_SINGLE_FACETS_MAPPING).find(
            (key) => key === category.systemName
          );

        if (keyInMultiple) {
          const modelKey =
            BASIC_INFO_FORM_MULTIPLE_FACETS_MAPPING[category.systemName];
          const value = { id: selectedFacet.facetId, name: facet.name };
          if (model[modelKey]) {
            model[modelKey].push(value);
          } else {
            model[modelKey] = [value];
          }
        } else if (keyInSingle) {
          const modelKey =
            BASIC_INFO_FORM_SINGLE_FACETS_MAPPING[category.systemName];

          model[modelKey] = selectedFacet.facetId;
        }
      });
    });

    return model;
  }

  private static mapBasePart(
    model: BasicInfoViewModel,
    basicInfo: IBasicInfoContract
  ): IBasicInfoContract {
    Object.keys(BASIC_INFO_FORM_BASE_MAPPING).forEach((key) => {
      const modelKey = BASIC_INFO_FORM_BASE_MAPPING[key];
      if (model[modelKey] !== undefined) {
        basicInfo[key] = model[modelKey];
      }
    });

    return basicInfo;
  }

  private static mapMeasurements(
    model: BasicInfoViewModel,
    basicInfo: IBasicInfoContract
  ): IBasicInfoContract {
    basicInfo.measurements = Object.keys(BASIC_INFO_FORM_MEASUREMENTS_MAPPING)
      .map((measurementKey) => {
        const modelKey = BASIC_INFO_FORM_MEASUREMENTS_MAPPING[measurementKey];
        let measurement: any = undefined;
        if (model[modelKey]) {
          measurement = {
            measurementType: measurementKey,
            value: model[modelKey],
          };
        }

        return measurement;
      })
      .filter(Boolean);

    return basicInfo;
  }

  private static mapFacets(
    model: BasicInfoViewModel,
    basicInfo: IBasicInfoContract
  ): IBasicInfoContract {
    const facets: { facetId: number; position?: number }[] = [];
    const facetMappingList = {
      ...BASIC_INFO_FORM_SINGLE_FACETS_MAPPING,
      ...BASIC_INFO_FORM_MULTIPLE_FACETS_MAPPING,
    };

    let positionCounter = 0;
    Object.keys(facetMappingList).forEach((facetKey) => {
      const modelKey = facetMappingList[facetKey];
      if (model[modelKey]) {
        if (Array.isArray(model[modelKey])) {
          facets.push(
            ...model[modelKey].map((item: any) => ({
              facetId: item.id,
              position: positionCounter++,
            }))
          );
        } else {
          const isFacetNotInTheList = !facets.some(
            (item) => item.facetId === model[modelKey]
          );
          isFacetNotInTheList &&
            facets.push({
              facetId: model[modelKey],
              position: positionCounter++,
            });
        }
      }
    });

    basicInfo.facets = facets;

    return basicInfo;
  }
}
